import { AiUsageRecordRow } from "../types/MeGenTypes";
import { RecordCostInfo } from "./aiModelCost";

// Only cost-report metadata is retained; never cache project credentials or graph state.
export type UsageProject = { id: number; name: string };
export type UsageProjectRunMap = { thread_id: string; project_id: number };
export type UsageProjectIndex = {
  projects: UsageProject[];
  threadProjects: Record<string, number>;
  failedProjects: number;
  conflictedThreads: number;
};
export type UsageProjectProgress = { projects: number; completed: number; total: number | null; loading: boolean };

export class UsageProjectLoadError extends Error {
  constructor(public reason: "cancelled" | "inconsistent" | "request") {
    super(`Project cost index: ${reason}`);
  }
}

/** Existing API has no total/cursor. Read every 100-row page; fail on visible offset drift. */
export const loadUsageProjectIndex = async (
  fetchProjects: (skip: number, limit: number) => Promise<UsageProject[]>,
  fetchRunMaps: (projectId: number) => Promise<UsageProjectRunMap[]>,
  isCurrent: () => boolean,
  onProgress: (progress: UsageProjectProgress) => void,
): Promise<UsageProjectIndex> => {
  const checkCurrent = () => { if (!isCurrent()) throw new UsageProjectLoadError("cancelled"); };
  const projects: UsageProject[] = [];
  const projectIds = new Set<number>();
  const pageSize = 100;
  for (let skip = 0; ; skip += pageSize) {
    checkCurrent();
    const page = await fetchProjects(skip, pageSize);
    checkCurrent();
    if (!Array.isArray(page) || page.length > pageSize) throw new UsageProjectLoadError("inconsistent");
    for (const project of page) {
      if (!project || !Number.isSafeInteger(project.id) || project.id <= 0 || typeof project.name !== "string" || projectIds.has(project.id)) throw new UsageProjectLoadError("inconsistent");
      projectIds.add(project.id);
      projects.push({ id: project.id, name: project.name });
    }
    onProgress({ projects: projects.length, completed: 0, total: null, loading: true });
    if (page.length < pageSize) break;
  }

  const threadProjects: Record<string, number> = Object.create(null);
  const conflicts = new Set<string>();
  let failedProjects = 0;
  let completed = 0;
  let nextProject = 0;
  // Bound API fanout; runmaps is a lightweight, uncapped array, not the 200-run history view.
  await Promise.all(Array.from({ length: Math.min(3, projects.length) }, async () => {
    while (nextProject < projects.length) {
      checkCurrent();
      const project = projects[nextProject++];
      let rows: UsageProjectRunMap[];
      try {
        rows = await fetchRunMaps(project.id);
        checkCurrent();
        if (!Array.isArray(rows) || rows.some((row) => !row || typeof row.thread_id !== "string" || !row.thread_id || row.project_id !== project.id)) throw new UsageProjectLoadError("inconsistent");
      } catch {
        checkCurrent();
        failedProjects++;
        completed++;
        onProgress({ projects: projects.length, completed, total: projects.length, loading: true });
        continue;
      }
      for (const row of rows) {
        if (conflicts.has(row.thread_id)) continue;
        if (Object.prototype.hasOwnProperty.call(threadProjects, row.thread_id) && threadProjects[row.thread_id] !== project.id) {
          delete threadProjects[row.thread_id];
          conflicts.add(row.thread_id);
        } else threadProjects[row.thread_id] = project.id;
      }
      completed++;
      onProgress({ projects: projects.length, completed, total: projects.length, loading: true });
    }
  }));
  checkCurrent();
  onProgress({ projects: projects.length, completed, total: projects.length, loading: false });
  return { projects, threadProjects, failedProjects, conflictedThreads: conflicts.size };
};

export const NO_THREAD_PROJECT = "no-thread";
export const UNMATCHED_PROJECT = "unmatched";
export const projectKeyOf = (record: AiUsageRecordRow, index?: UsageProjectIndex): string => {
  if (!record.thread_id) return NO_THREAD_PROJECT;
  const id = index && Object.prototype.hasOwnProperty.call(index.threadProjects, record.thread_id) ? index.threadProjects[record.thread_id] : undefined;
  return id !== undefined ? `project:${id}` : UNMATCHED_PROJECT;
};

export type ProjectCostSummary = {
  calls: number;
  errors: number;
  cost: number;
  missingPriceCalls: number;
  unknownQuantityCalls: number;
  estimatedCalls: number;
  lastUsed: string;
  records: AiUsageRecordRow[];
};
export type ProjectThreadCost = ProjectCostSummary & { key: string; threadId: string | null };
export type ProjectUsageCost = ProjectCostSummary & { key: string; projectId: number | null; name: string; threads: ProjectThreadCost[] };

const emptySummary = (): ProjectCostSummary => ({ calls: 0, errors: 0, cost: 0, missingPriceCalls: 0, unknownQuantityCalls: 0, estimatedCalls: 0, lastUsed: "", records: [] });
const addRecord = (summary: ProjectCostSummary, row: AiUsageRecordRow, info: RecordCostInfo) => {
  summary.calls++;
  if (row.status === "error") summary.errors++;
  summary.cost += info.result.cost;
  if (info.result.missingRate) summary.missingPriceCalls++;
  if (info.result.quantityUnknown) summary.unknownQuantityCalls++;
  if (info.result.estimated) summary.estimatedCalls++;
  if (row.created_at && Number.isFinite(Date.parse(row.created_at)) && (!summary.lastUsed || Date.parse(row.created_at) > Date.parse(summary.lastUsed))) summary.lastUsed = row.created_at;
  summary.records.push(row);
};

/** One raw record contributes once to its parent and once to its child; never sum both. */
export const aggregateProjectUsage = (records: AiUsageRecordRow[], index: UsageProjectIndex | undefined, costInfoOf: (row: AiUsageRecordRow) => RecordCostInfo): ProjectUsageCost[] => {
  const projects = new Map<string, ProjectUsageCost>();
  const projectNames = new Map(index?.projects.map((project) => [project.id, project.name]));
  const threads = new Map<string, Map<string, ProjectThreadCost>>();
  for (const record of records) {
    const key = projectKeyOf(record, index);
    let project = projects.get(key);
    if (!project) {
      const projectId = key.startsWith("project:") ? Number(key.slice(8)) : null;
      project = { ...emptySummary(), key, projectId, name: projectId !== null ? projectNames.get(projectId) || "" : "", threads: [] };
      projects.set(key, project);
      threads.set(key, new Map());
    }
    const threadKey = record.thread_id ? `thread:${record.thread_id}` : NO_THREAD_PROJECT;
    let thread = threads.get(key)!.get(threadKey);
    if (!thread) {
      thread = { ...emptySummary(), key: threadKey, threadId: record.thread_id || null };
      threads.get(key)!.set(threadKey, thread);
      project.threads.push(thread);
    }
    const info = costInfoOf(record);
    addRecord(project, record, info);
    addRecord(thread, record, info);
  }
  const sortByCost = (a: ProjectCostSummary, b: ProjectCostSummary) => b.cost - a.cost || b.calls - a.calls;
  // Keep exception buckets after every real project, regardless of their cost/call count.
  const projectOrder = (project: ProjectUsageCost) => project.key === UNMATCHED_PROJECT ? 1 : project.key === NO_THREAD_PROJECT ? 2 : 0;
  projects.forEach((project) => project.threads.sort(sortByCost));
  return Array.from(projects.values()).sort((a, b) => projectOrder(a) - projectOrder(b) || sortByCost(a, b));
};
